// Applies a validated snapshot application plan (./snapshotApplicationPlan.ts) onto a target data
// context: assigns the sanitized selection/restriction filters, recomputes the item maps from
// their steps, then replays the sanitized manual-toggle diffs on top. See ./index.ts for the
// feature-level overview and the "manual diff" design rationale; this module only reads/replays
// the existing, unmodified selection engine primitives (`calculateEnSelectionFilter`,
// `applyManualItemToggle`, etc.) and never mutates them.

import { TpCanonicalTypeInfo, TpDataContext } from '../../interfaces';
import { applyManualItemToggle } from '../../analyticsDataSlice';
import { updateCanonicalTypeRestrictionData } from '../../analyticsDataStorage';
import { calculateEnSelectionFilter } from '../selectionEnFilters';
import { applyEnRestrictionFilter } from '../selectionUtils';
import {
  TpCanonicalTypeSnapshotApplicationPlan,
  TpSelectionFilterApplicationPlan,
  TpSnapshotApplicationPlan,
} from './snapshotApplicationPlan';

/** Flips membership of a single item in `restrictedItemsMap` (no "reset to null" special case - unlike selection, an empty restriction map is a legitimate "restricted to nothing" state). */
function flipRestrictedItemMembership(canonicalTypeInfo: TpCanonicalTypeInfo, itemId: string) {
  if (!canonicalTypeInfo.restrictedItemsMap) canonicalTypeInfo.restrictedItemsMap = {};
  if (canonicalTypeInfo.restrictedItemsMap[itemId]) delete canonicalTypeInfo.restrictedItemsMap[itemId];
  else canonicalTypeInfo.restrictedItemsMap[itemId] = true;
}

/**
 * Applies one canonical type's snapshot entry onto the corresponding (same-id) canonical type of
 * the target data context using a sanitized plan: assigns the selection filter, recomputes `selectedItemsMap`
 * from the sanitized steps, then replays the sanitized manual-toggle diff on top; repeats the same
 * for the restriction layer.
 */
export function applySnapshotToCanonicalTypeInfo(
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
  plan: TpCanonicalTypeSnapshotApplicationPlan,
) {
  // ----- Restriction -----
  if (plan.restrictionFilterPlan && plan.restrictionFilterPlan.filter) {
    canonicalTypeInfo.restrictionFilter = plan.restrictionFilterPlan.filter;
    applyEnRestrictionFilter(dataContext, canonicalTypeInfo);
    const manualIds = plan.restrictionFilterPlan.manualToggleIds;
    if (manualIds.length > 0) {
      for (const itemId of manualIds) flipRestrictedItemMembership(canonicalTypeInfo, itemId);
      updateCanonicalTypeRestrictionData(canonicalTypeInfo);
    }
    canonicalTypeInfo.restrictionFilter!.manualSelection = manualIds.length > 0;
  } else {
    canonicalTypeInfo.restrictionFilter = null;
    canonicalTypeInfo.restrictedItemsMap = null;
    updateCanonicalTypeRestrictionData(canonicalTypeInfo);
  }

  applySelectionFilterToCanonicalTypeInfo(dataContext, canonicalTypeInfo, plan.selectionFilterPlan);
}

/** Applies only the selection portion of a snapshot plan. */
export function applySelectionFilterToCanonicalTypeInfo(
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
  plan: TpSelectionFilterApplicationPlan,
) {
  canonicalTypeInfo.selectionFilter = plan.filter;
  calculateEnSelectionFilter(dataContext, canonicalTypeInfo);
  for (const itemId of plan.manualToggleIds) applyManualItemToggle(canonicalTypeInfo, itemId);
}

/**
 * Applies a full-data-context snapshot to a target data context using a sanitized plan: for each
 * canonical type, assigns the selection filter, recomputes `selectedItemsMap` from the sanitized
 * steps, then replays the sanitized manual-toggle diff on top; repeats the same for the restriction
 * layer.
 */
export function applySnapshotToDataContext(dataContext: TpDataContext, plan: TpSnapshotApplicationPlan) {
  for (const [canonicalTypeId, snapshotPlanEntry] of Object.entries(plan.canonicalTypeSelections)) {
    const canonicalTypeInfo = dataContext.canonicalTypes[canonicalTypeId];
    if (!canonicalTypeInfo) {
      console.warn(`Entity "${canonicalTypeId}": not present in target data.`);
      continue;
    }
    applySnapshotToCanonicalTypeInfo(dataContext, canonicalTypeInfo, snapshotPlanEntry);
  }
}
